/**
 * `/__gallery` — the design system, on one page. **Development only.**
 *
 * `.docs/09-design-system.md` §3: "Excluded from production builds (route guard
 * + a test on `dist`)." Both halves are here, and they are not the same
 * mechanism:
 *
 *   - **The guard** is `beforeLoad`, so a production build that somehow shipped
 *     the code still answers `/__gallery` with the portal's own not-found page.
 *   - **The fold** is `component`. Vite replaces `import.meta.env.DEV` with the
 *     literal `false` in a production build, so the ternary below collapses to
 *     `NotFound` and the `import()` of the gallery is dead code, removed with
 *     the branch it is in — the stories, the fixtures and every primitive they
 *     are the only consumer of never enter the production graph at all.
 *     `test/web.build.test.ts` is what proves it, by looking for the gallery's
 *     own strings in what was emitted.
 *
 * **A dynamic import, since session 46.** It was a static import whose binding the
 * fold left unreferenced, and that drops a module only if Rollup can prove the
 * module has no side effects. Every story used to be such a module; the rich-text
 * editor's are not — TipTap and `@fareast/rich-text` build their extensions at
 * module level — so the static import kept `prosemirror-view` and TipTap's core in
 * the production bundle with nothing using them. Dead code is dropped whatever it
 * imports, which is a guarantee the next family will not have to think about.
 *
 * A `notFound()` from `beforeLoad` alone would not have done that: the component
 * would still be in the bundle, one route away from a reader.
 */

import { createFileRoute, lazyRouteComponent, notFound } from '@tanstack/react-router'

import { NotFound } from '../errors/NotFound.js'

export const Route = createFileRoute('/__gallery')({
  /*
   * No automatic chunk for this one. `autoCodeSplitting` would move `component`
   * into a chunk of its own, and the ternary below would be folded *there* —
   * where the gallery graph is already the chunk's only reason to exist, so
   * nothing is dropped. Kept in the reference file, `import.meta.env.DEV` becomes
   * the literal `false` in a production build and the `import()` goes with the
   * branch it is in.
   */
  codeSplitGroupings: [],
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound()
  },
  component: import.meta.env.DEV
    ? lazyRouteComponent(() => import('../gallery/Gallery.js'), 'Gallery')
    : NotFound,
})
